/**
 * ============================================================================
 * DOCUMENTS STORE - FETCH OPERATIONS
 * ============================================================================
 * 
 * Handles fetching documents from the backend API with local-first approach.
 * 
 * Features:
 * - Returns cached data immediately if available
 * - Background sync to keep data fresh
 * - Falls back to server fetch if no cache
 * 
 * @module documents/api/fetch
 */

import { apiService } from "@/lib/api-functions/api-service";
import { documentStore } from "../store";
import { getCachedDocumentItems } from "../cache/byGame";
import type { DocumentItem, ToastFunction } from "../types";

// ============================================================================
// INTERNAL FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch documents from server and update store.
 * @internal
 */
async function fetchDocumentItemsFromServer(
  gameId: string,
  toast?: ToastFunction
): Promise<DocumentItem[]> {
  // Validate authentication
  const token = await apiService.getToken();
  if (!token) {
    console.error('[fetchDocumentItems] No authentication token available');
    throw new Error('No authentication token available');
  }

  // Fetch data from API
  const result = await apiService.fetchDocumentItems(gameId);

  if (!result.success || !result.data) {
    console.error('[fetchDocumentItems] API call failed:', result.error);
    throw new Error(result.error || 'Failed to fetch documents');
  }

  const documents = result.data;

  // Sync the store with the fetched data
  await documentStore.syncFromRemote(async () => documents);

  return documents;
}

/**
 * Background sync with server - doesn't throw errors to avoid interrupting UI.
 * @internal
 */
async function syncDocumentItemsInBackground(
  gameId: string,
  toast?: ToastFunction
): Promise<void> {
  try {
    await fetchDocumentItemsFromServer(gameId);
  } catch (error) {
    console.warn('[syncDocumentItemsInBackground] Background sync failed:', error);
    // Don't show toast for background sync failures to avoid disrupting user experience
  }
}

// ============================================================================
// PUBLIC FETCH OPERATIONS
// ============================================================================

/**
 * Fetch documents with local-first approach.
 * 
 * Returns cached data immediately if available, then syncs with server
 * in the background to keep data fresh.
 * 
 * @param gameId - The game ID to fetch documents for
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to array of documents
 * 
 * @example
 * ```ts
 * // Fetch with local-first approach
 * const documents = await fetchDocumentItems(gameId);
 * ```
 */
export async function fetchDocumentItems(
  gameId: string,
  toast?: ToastFunction
): Promise<DocumentItem[]> {
  try {
    // First, try to get cached data for immediate response
    const cachedData = getCachedDocumentItems(gameId);
    
    // If we have cached data, return it immediately and sync in background
    if (cachedData.length > 0) {
      // Start background sync but don't wait for it
      syncDocumentItemsInBackground(gameId, toast);
      return cachedData;
    }

    // If no cached data, fetch from server
    return await fetchDocumentItemsFromServer(gameId, toast);
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch documents';
    console.error('[fetchDocumentItems] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
